import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_PREFIX = "columnPrefs:";

const readStoredPrefs = (storageKey) => {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.order) || !Array.isArray(parsed?.hidden)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeStoredPrefs = (storageKey, prefs) => {
  if (!storageKey) return;
  localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, JSON.stringify(prefs));
};

// A table and its toolbar (e.g. Table + Vpagination) each mount their own
// instance of this hook for the same storageKey. Persisting from one instance
// needs to update the other immediately (not just on next reload), so
// instances sharing a storageKey are kept in sync via this in-memory registry.
const subscribers = new Map();

const subscribe = (storageKey, listener) => {
  if (!storageKey) return () => {};
  if (!subscribers.has(storageKey)) subscribers.set(storageKey, new Set());
  subscribers.get(storageKey).add(listener);
  return () => {
    subscribers.get(storageKey)?.delete(listener);
  };
};

const notifySubscribers = (storageKey, prefs) => {
  subscribers.get(storageKey)?.forEach((listener) => listener(prefs));
};

// Reconciles a saved {order, hidden} (keyed by header label) against the
// current header list, so columns added/removed in later releases don't
// break or get lost - new labels are appended (visible), stale ones dropped.
const reconcile = (headerLabels, stored) => {
  if (!stored) {
    return { order: [...headerLabels], hidden: [] };
  }
  const currentSet = new Set(headerLabels);
  const order = stored.order.filter((label) => currentSet.has(label));
  headerLabels.forEach((label) => {
    if (!order.includes(label)) order.push(label);
  });
  const hidden = stored.hidden.filter((label) => currentSet.has(label));
  return { order, hidden };
};

// Per-screen column show/hide + reorder, persisted to localStorage.
// `headerLabels` is the default (code-defined) header order - column
// identity is the label string itself, matching the existing `sortables`
// convention rather than introducing a new column-id concept.
export const useColumnPreferences = (storageKey, headerLabels) => {
  // Holds only what's actually persisted (or null before anything is saved).
  // Table data frequently starts as [] and is replaced once an API call
  // resolves, so the *reconciled* column list (below) must be recomputed on
  // every render from the current headerLabels - baking reconciliation into
  // this initial state (a one-time useState initializer) would freeze the
  // column list at whatever headerLabels was on first mount, permanently
  // hiding every column if that first render had none yet.
  const [rawPrefs, setRawPrefs] = useState(() => readStoredPrefs(storageKey));

  useEffect(() => {
    setRawPrefs(readStoredPrefs(storageKey));
    return subscribe(storageKey, setRawPrefs);
  }, [storageKey]);

  const prefs = useMemo(() => reconcile(headerLabels, rawPrefs), [headerLabels, rawPrefs]);

  const persist = useCallback(
    (next) => {
      setRawPrefs(next);
      writeStoredPrefs(storageKey, next);
      notifySubscribers(storageKey, next);
    },
    [storageKey]
  );

  const allColumns = useMemo(
    () =>
      prefs.order
        .map((label) => ({
          label,
          index: headerLabels.indexOf(label),
          visible: !prefs.hidden.includes(label),
        }))
        .filter((col) => col.index !== -1),
    [prefs, headerLabels]
  );

  const visibleIndices = useMemo(
    () => allColumns.filter((col) => col.visible).map((col) => col.index),
    [allColumns]
  );

  const toggleVisibility = useCallback(
    (label) => {
      const hidden = prefs.hidden.includes(label)
        ? prefs.hidden.filter((l) => l !== label)
        : [...prefs.hidden, label];
      persist({ order: prefs.order, hidden });
    },
    [prefs, persist]
  );

  const reorder = useCallback(
    (newLabelOrder) => {
      persist({ order: newLabelOrder, hidden: prefs.hidden });
    },
    [prefs, persist]
  );

  const resetToDefault = useCallback(() => {
    persist({ order: [...headerLabels], hidden: [] });
  }, [headerLabels, persist]);

  return { visibleIndices, allColumns, toggleVisibility, reorder, resetToDefault };
};
